import React from 'react';
import { Link } from 'react-router-dom';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-orange-100">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <span className="text-2xl">🎨</span>
              <span className="text-xl font-bold text-gray-800">
                Kitchen<span className="text-orange-500">Canvas</span>
              </span>
            </Link>
            <Link to="/" className="text-sm text-gray-500 hover:text-orange-500 transition-colors flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-10 text-center">
          <span className="text-5xl block mb-4">🔒</span>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-500">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 space-y-8 text-gray-700">
          
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 flex items-start gap-4">
            <span className="text-2xl">✨</span>
            <div>
              <h3 className="font-bold text-green-800 mb-1">The Short Version</h3>
              <p className="text-green-700 text-sm">Your recipes and data stay on YOUR computer. We don't see them, we don't store them, we don't share them. Simple as that!</p>
            </div>
          </div>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="text-orange-500">1.</span> What Information We Collect
            </h2>
            <p className="mb-4 leading-relaxed">
              Kitchen Canvas is designed with privacy at its core. Here's what you should know:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                <span className="text-xl">💾</span>
                <div>
                  <p className="font-medium text-gray-900">Local Storage Only</p>
                  <p className="text-sm text-gray-600">All your recipes, ingredients, and preferences are stored locally on your device. Nothing is uploaded to any server.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                <span className="text-xl">📊</span>
                <div>
                  <p className="font-medium text-gray-900">Basic Analytics</p>
                  <p className="text-sm text-gray-600">We collect anonymized usage statistics (like which features are popular) to improve the app. This data cannot identify you.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                <span className="text-xl">⬇️</span>
                <div>
                  <p className="font-medium text-gray-900">Download Data</p>
                  <p className="text-sm text-gray-600">When you download the app, standard web logs (IP address, browser info) may be collected for security purposes.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="text-orange-500">2.</span> How We Use This Information
            </h2>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <span className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center text-orange-500 text-sm">✓</span>
                <span>Improve app features and performance</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center text-orange-500 text-sm">✓</span>
                <span>Fix bugs and technical issues</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center text-orange-500 text-sm">✓</span>
                <span>Understand which features matter most to users</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center text-orange-500 text-sm">✓</span>
                <span>Protect against misuse and security threats</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="text-orange-500">3.</span> Data Security
            </h2>
            <p className="leading-relaxed">
              Since Kitchen Canvas works entirely offline and stores data on your local device, your information remains under your complete control. Any analytics data we do collect is anonymized and secured using industry-standard practices.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="text-orange-500">4.</span> Third-Party Services
            </h2>
            <p className="leading-relaxed">
              We may use third-party analytics tools to understand app usage patterns. These services only receive anonymized, aggregated data and cannot identify individual users.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="text-orange-500">5.</span> Children's Privacy
            </h2>
            <p className="leading-relaxed">
              Kitchen Canvas is not directed at children under 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="text-orange-500">6.</span> Changes to This Policy
            </h2>
            <p className="leading-relaxed">
              We may update this Privacy Policy from time to time. We'll post any changes on this page with an updated date. Continued use of the app after changes constitutes acceptance of the new policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="text-orange-500">7.</span> Contact Us
            </h2>
            <p className="leading-relaxed">
              Questions about our privacy practices? We're happy to help! Reach out to us at{' '}
              <a href="mailto:support@kitchen-canvas.com" className="text-orange-500 hover:underline font-medium">
                support@kitchen-canvas.com
              </a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-orange-100 mt-12">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">🎨</span>
            <span className="font-bold text-gray-800">KitchenCanvas</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/privacy" className="text-orange-500 font-medium">Privacy</Link>
            <Link to="/terms" className="hover:text-orange-500 transition-colors">Terms</Link>
          </div>
          <div className="text-sm text-gray-400">© {new Date().getFullYear()} Kitchen Canvas</div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
